import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Route, Routes, unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
// mui
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
// theme
import { theme } from './packages';
import OneSignal from 'react-onesignal';
import Intercom from '@intercom/messenger-js-sdk';
// redux
import createStore from './redux/store';
import { getAuth } from './redux/modules/auth/selectors';
import history from '././utils/history';
import { adminRoutes, providerRoutes } from './routes';
// pages
import Login from './pages/login';
import AdminDashboard from './pages/admin/dashboard';
import Schedule from './pages/provider/schedule';
import SnackContainer from './layouts/snackbar';
import TwoFactorAuthentication from './pages/provider/two-factor-authentication';
import { ForgotPassword } from './pages/provider/forgot-password/ForgotPassword';
// One Signal
import SocketListener from './utils/SocketListener';
import { getSocketState } from './redux/modules/socket/selectors';
import getConfig from './config';
import SocketScreens from './utils/SocketScreens';
import { InAppNotification } from './utils/InAppNotifications';
import ErrorPage from './pages/admin/Error-Page';
import { socketActionCreators } from './redux/modules/socket';
import { SUPPORTED_AUTHORITIES } from './constants/CommonConstants';
import ConfidantErrorBoundary from './components/error-boundary/ConfidantErrorBoundary';
import { authActionCreators } from './redux/modules/auth';

export const { store, persistor } = createStore();

const initOneSignal = async () => {
  try {
    await OneSignal.init({
      appId: getConfig.api.oneSignalAppId,
      allowLocalhostAsSecureOrigin: true,
    });
    console.log('One Signal Enabled', await OneSignal.isPushNotificationsEnabled());
    console.log('OneSignal initialized!');
  } catch (error) {
    console.log('One Signal Init Error', error);
  }
};

const App = () => {
  const { isAuthenticated, isAdmin, meta } = useSelector(getAuth);
  const { navigate, navigateData } = useSelector(getSocketState);
  const { pathname, search } = window.location;
  const dispatch = useDispatch();

  if (isAuthenticated && meta?.aliasEmail && meta?.userId)
    Intercom({
      app_id: 'prpz3w0e',
      user_id: meta?.userId, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
      name: meta?.nickName, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
      email: meta?.aliasEmail, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
      created_at: new Date(parseInt(meta?.userId.substring(0, 8), 16) * 1000).getTime(), // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
    });

  useEffect(() => {
    if (navigate) {
      switch (navigateData.screen) {
        case SocketScreens.CHAT:
          if (history.location.pathname !== '/provider/chats') {
            history.push(`/provider/chats`, {
              tabType: navigateData.payload.tabType,
              chatId: navigateData.payload.senderId,
            });
          }
          break;
        case SocketScreens.ADMINCHAT:
          if (history.location.pathname !== '/admin/chats') {
            history.push(`/admin/chats`, {
              tabType: navigateData.payload.tabType,
              chatId: navigateData.payload.senderId,
            });
          }
          break;
        case SocketScreens.ADMIN_APPOINTMENT:
          history.push(`/admin/appointments/${navigateData.payload.appointmentType}`, {
            appointmentId: navigateData.payload.appointmentId,
          });
          break;
        default:
          break;
      }
      store.dispatch(socketActionCreators.navigationComplete());
    }
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      SocketListener.initSocket(meta, store);
      void initOneSignal();
      if (isAdmin) document.title = 'Confidant Health - We Care';
      else document.title = 'Confidant Health - EMR 2.0';
    } else {
      if (!pathname.includes('login')) {
        dispatch(authActionCreators.authenticatedLinkNavigation(pathname + search));
      }
      history.push(`/login`);
    }
  }, [isAuthenticated]);

  let routes;
  if (isAdmin) {
    if (meta.authority === SUPPORTED_AUTHORITIES.ADMIN) {
      routes = adminRoutes.filter(item => {
        return item.authority.includes('ADMIN');
      });
    }
    if (
      meta.authority === SUPPORTED_AUTHORITIES.CARE_NAVIGATOR ||
      meta.authority === SUPPORTED_AUTHORITIES.BILLING_SUPPORT
    ) {
      routes = adminRoutes.filter(item => {
        return item.authority.includes(meta.authority);
      });
    }
  } else {
    routes = providerRoutes;
  }

  return (
    <HistoryRouter history={history}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/twofactorauthentication" element={<TwoFactorAuthentication />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* <Route  */}
        {isAuthenticated && (
          <>
            {routes.map(({ path, component: Component }) => (
              <Route path={path} element={<Component />} key={path} />
            ))}
          </>
        )}
        <Route path="/" element={isAdmin ? <AdminDashboard /> : <Schedule />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </HistoryRouter>
  );
};

ReactDOM.render(
  <React.StrictMode>
    <ConfidantErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <InAppNotification />
              <App />
              <SnackContainer />
            </ThemeProvider>
          </StyledEngineProvider>
        </PersistGate>
      </Provider>
    </ConfidantErrorBoundary>
  </React.StrictMode>,
  document.getElementById('root'),
);
